import { DSIcon, DSTypography } from '@eiv2/design-system';
import { formatNumber, formatPercent } from '../services/format';
import { buildFunnelView } from '../services/funnelView';
import type { FunnelStep } from '../types/api';

const FUNNEL_BAR_TONES = [
  'bg-brand-primary-3',
  'bg-brand-primary-3/75',
  'bg-[#8E5CE3]',
  'bg-[#A078EB]',
  'bg-[#C4B5FD]',
  'bg-[#D3C0FF]'
] as const;

function barToneClass(event: string, index: number): string {
  if (event === 'payment_approved') return 'bg-feedback-success';
  if (event === 'checkout_abandoned' || event === 'payment_failed') {
    return 'bg-feedback-danger';
  }
  return FUNNEL_BAR_TONES[index % FUNNEL_BAR_TONES.length] ?? 'bg-brand-primary-3';
}

export function FunnelSummary({ steps }: { steps: readonly FunnelStep[] }) {
  const view = buildFunnelView(steps);

  if (!view.hasData) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 px-4">
        <span
          aria-hidden
          className="inline-flex size-12 items-center justify-center rounded-2xl bg-gray-2 text-gray-5"
        >
          <DSIcon name="Filter" size={24} />
        </span>
        <DSTypography as="p" className="max-w-sm text-center text-[13px] font-normal text-gray-5">
          Sem jornadas no funil para o período filtrado.
        </DSTypography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        {view.rows.map((row, index) => (
          <div key={row.event} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <div className="min-w-0">
              <div className="mb-1 flex items-center justify-between gap-2">
                <DSTypography as="p" className="truncate text-[13px] font-semibold text-dark-3">
                  {row.label}
                </DSTypography>
                <DSTypography as="p" className="shrink-0 text-[12px] font-normal text-gray-5">
                  {formatNumber(row.count)}
                  {row.rate !== null ? ` · ${formatPercent(row.rate)}` : ''}
                </DSTypography>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-2">
                <div
                  className={`h-full rounded-full transition-[width] duration-300 ease-out ${barToneClass(row.event, index)}`}
                  style={{ width: `${row.widthPercent}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <DSTypography as="p" className="text-[12px] font-normal text-gray-5">
        Contagens independentes por etapa no período. Diferenças entre etapas não medem abandono.
        Percentuais relativos às aberturas, quando disponíveis.
      </DSTypography>
    </div>
  );
}
